import { useRef, useCallback, useState } from 'react';
import turfDistance from '@turf/distance';

export interface Measurement {
  id: string;
  points: [number, number][];  // [lng, lat][]
  segments: number[];           // distance per segment in miles
  total: number;                // total distance in miles
}

export interface UseMeasurementReturn {
  activeRef: React.MutableRefObject<boolean>;
  measurementsRef: React.MutableRefObject<Measurement[]>;
  currentPointsRef: React.MutableRefObject<[number, number][]>;
  measurementCount: number;
  handleMapClick: (lngLat: { lng: number; lat: number }) => void;
  finishMeasurement: () => void;
  undoLastPoint: () => void;
  cancelCurrent: () => void;
  clearAll: () => void;
  getLineGeoJSON: () => GeoJSON.FeatureCollection;
  getPointGeoJSON: () => GeoJSON.FeatureCollection;
  getPreviewGeoJSON: (cursorLngLat: [number, number] | null) => GeoJSON.FeatureCollection;
}

export function formatDistance(miles: number): string {
  if (miles < 1) {
    const feet = miles * 5280;
    return `${Math.round(feet / 10) * 10} ft`;
  }
  return `${miles.toFixed(1)} mi`;
}

const TURF_MILES = { units: 'miles' as const };

function calcSegmentDistance(a: [number, number], b: [number, number]): number {
  return turfDistance(a, b, TURF_MILES);
}

export function useMeasurement(): UseMeasurementReturn {
  const activeRef = useRef(false);
  const measurementsRef = useRef<Measurement[]>([]);
  const currentPointsRef = useRef<[number, number][]>([]);
  const idCounterRef = useRef(0);
  const [measurementCount, setMeasurementCount] = useState(0);

  const handleMapClick = useCallback((lngLat: { lng: number; lat: number }) => {
    const point: [number, number] = [lngLat.lng, lngLat.lat];
    currentPointsRef.current = [...currentPointsRef.current, point];
  }, []);

  const finishMeasurement = useCallback(() => {
    const pts = currentPointsRef.current;
    if (pts.length < 2) {
      // Not enough points to form a line
      currentPointsRef.current = [];
      return;
    }

    const segments: number[] = [];
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      const dist = calcSegmentDistance(pts[i - 1], pts[i]);
      segments.push(dist);
      total += dist;
    }

    const measurement: Measurement = {
      id: `m-${++idCounterRef.current}`,
      points: [...pts],
      segments,
      total,
    };

    measurementsRef.current = [...measurementsRef.current, measurement];
    currentPointsRef.current = [];
    setMeasurementCount(measurementsRef.current.length);
  }, []);

  const undoLastPoint = useCallback(() => {
    if (currentPointsRef.current.length > 0) {
      currentPointsRef.current = currentPointsRef.current.slice(0, -1);
    }
  }, []);

  const cancelCurrent = useCallback(() => {
    currentPointsRef.current = [];
  }, []);

  const clearAll = useCallback(() => {
    measurementsRef.current = [];
    currentPointsRef.current = [];
    setMeasurementCount(0);
  }, []);

  const getLineGeoJSON = useCallback((): GeoJSON.FeatureCollection => {
    const features: GeoJSON.Feature[] = [];

    // Completed measurements
    for (const m of measurementsRef.current) {
      if (m.points.length >= 2) {
        features.push({
          type: 'Feature',
          properties: {
            id: m.id,
            total: m.total,
            label: formatDistance(m.total),
            type: 'completed',
          },
          geometry: {
            type: 'LineString',
            coordinates: m.points,
          },
        });
      }
    }

    // Current in-progress measurement
    const pts = currentPointsRef.current;
    if (pts.length >= 2) {
      let total = 0;
      for (let i = 1; i < pts.length; i++) {
        total += calcSegmentDistance(pts[i - 1], pts[i]);
      }
      features.push({
        type: 'Feature',
        properties: {
          id: 'current',
          total,
          label: formatDistance(total),
          type: 'in_progress',
        },
        geometry: {
          type: 'LineString',
          coordinates: pts,
        },
      });
    }

    return { type: 'FeatureCollection', features };
  }, []);

  const getPointGeoJSON = useCallback((): GeoJSON.FeatureCollection => {
    const features: GeoJSON.Feature[] = [];

    // Points from completed measurements
    for (const m of measurementsRef.current) {
      for (let i = 0; i < m.points.length; i++) {
        features.push({
          type: 'Feature',
          properties: {
            measurementId: m.id,
            index: i,
            isEndpoint: i === m.points.length - 1,
            label: i === m.points.length - 1 ? formatDistance(m.total) : null,
          },
          geometry: {
            type: 'Point',
            coordinates: m.points[i],
          },
        });
      }
    }

    // Points from current in-progress measurement
    const pts = currentPointsRef.current;
    let runningTotal = 0;
    for (let i = 0; i < pts.length; i++) {
      if (i > 0) runningTotal += calcSegmentDistance(pts[i - 1], pts[i]);
      features.push({
        type: 'Feature',
        properties: {
          measurementId: 'current',
          index: i,
          isEndpoint: false,
          label: i > 0 ? formatDistance(runningTotal) : null,
        },
        geometry: {
          type: 'Point',
          coordinates: pts[i],
        },
      });
    }

    return { type: 'FeatureCollection', features };
  }, []);

  const getPreviewGeoJSON = useCallback((cursorLngLat: [number, number] | null): GeoJSON.FeatureCollection => {
    const pts = currentPointsRef.current;
    if (!cursorLngLat || pts.length === 0) {
      return { type: 'FeatureCollection', features: [] };
    }

    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [pts[pts.length - 1], cursorLngLat],
        },
      }],
    };
  }, []);

  return {
    activeRef,
    measurementsRef,
    currentPointsRef,
    measurementCount,
    handleMapClick,
    finishMeasurement,
    undoLastPoint,
    cancelCurrent,
    clearAll,
    getLineGeoJSON,
    getPointGeoJSON,
    getPreviewGeoJSON,
  };
}
